// Shared domain types for The Pale Moonlight performance player.

import type { ClickSound } from '../audio/clickSounds';

export type PlaybackMode = 'tracks-click' | 'metronome' | 'tracks-only';

export type SongStatus = 'ok' | 'missing' | 'unreadable' | 'unsupported' | 'mono-warning' | 'checking';

export type AudioFileFormat = 'wav' | 'mp3';

export interface MetronomeSettings {
  bpm: number; // 30-300
  beatsPerMeasure: number; // numerator, e.g. 4
  beatUnit: 4 | 8; // denominator: quarter or eighth-note beat
  subdivision: 1 | 2 | 4; // 1 = quarter notes, 2 = eighths, 4 = sixteenths per beat
  accentFirstBeat: boolean;
  clickVolume: number; // 0-1
  countInMeasures: 0 | 1 | 2 | 4;
  visualBeatIndicator: boolean;
  sound: ClickSound;
  /** Which output side the click plays on — 'right' matches the historical
   * default (leaves the left/PA channel silent for a click track running
   * alongside a stereo breakout cable), 'left'/'stereo' are for practice
   * setups with no such split to worry about. */
  clickChannel: 'stereo' | 'left' | 'right';
}

export const DEFAULT_METRONOME_SETTINGS: MetronomeSettings = {
  bpm: 120,
  beatsPerMeasure: 4,
  beatUnit: 4,
  subdivision: 1,
  accentFirstBeat: true,
  clickVolume: 0.8,
  countInMeasures: 1,
  visualBeatIndicator: true,
  sound: 'beep',
  clickChannel: 'right',
};

/** Standalone practice metronome preset — independent of setlists/songs. */
export interface MetronomePresetRecord {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  settings: MetronomeSettings;
}

/** Free-text notes attached to a marker, one field per role — e.g. a
 * singer's lyrics, a guitarist's chords, or anything else that doesn't fit
 * either (cues, reminders), plus whatever user-defined tabs (see
 * CustomNoteTab below) exist at the time. Purely local text: there's no
 * sync between band members' own copies of the app, so editing a note only
 * ever changes it on whoever's device is doing the editing — the same way
 * every other field in this app already works. A bandmate who imports a
 * shared setlist ZIP sees whatever notes were filled in at export time, and
 * can freely edit their own copy from there with no effect on anyone
 * else's.
 *
 * Keyed by MarkerNoteTab (a plain string, not a fixed literal union) rather
 * than 3 named fields — see CustomNoteTab's own comment for why this needs
 * to be open-ended. The 3 BUILT_IN_NOTE_TABS keys are always present (every
 * marker is backfilled with EMPTY_MARKER_NOTES on load/creation — see
 * store.ts's backfillMarker/addMarker), but a CUSTOM tab's key may be
 * entirely absent from an older marker created before that tab existed, or
 * one whose content was never touched — always read through
 * markerNoteText/markerNoteFormat/markerNoteSheet below rather than
 * indexing these records directly, or a missing key reads as `undefined`
 * instead of the empty default a caller almost certainly wants. */
export type MarkerNoteTab = string;
export type MarkerNotes = Record<MarkerNoteTab, string>;

export const EMPTY_MARKER_NOTES: MarkerNotes = { lyrics: '', chords: '', general: '' };

export type MarkerNoteFormat = 'text' | 'sheet';

/** A deliberately small, safe subset of spreadsheet formatting. Google
 * Sheets clipboard HTML can contain a large amount of app-specific markup;
 * the player only needs the musical layout, so we retain cell text, merges,
 * emphasis, and alignment and render it with the app's own table styling. */
export interface MarkerSheetCell {
  text: string;
  colSpan?: number;
  rowSpan?: number;
  bold?: boolean;
  align?: 'left' | 'center' | 'right';
}

export interface MarkerSheetData {
  rows: MarkerSheetCell[][];
}

export type MarkerNoteFormats = Record<MarkerNoteTab, MarkerNoteFormat>;
export type MarkerNoteSheets = Record<MarkerNoteTab, MarkerSheetData>;

export const EMPTY_MARKER_NOTE_FORMATS: MarkerNoteFormats = { lyrics: 'text', chords: 'text', general: 'text' };
export const EMPTY_MARKER_NOTE_SHEETS: MarkerNoteSheets = {
  lyrics: { rows: [] },
  chords: { rows: [] },
  general: { rows: [] },
};

/** Safe accessors for a marker's per-tab note content — ALWAYS use these
 * instead of indexing `marker.notes`/`noteFormats`/`noteSheets` directly
 * with a tab id that might be a user-defined custom tab, since a marker
 * created (or last edited) before that tab existed simply won't have the
 * key at all. The 3 built-in tabs never actually need the fallback (they're
 * guaranteed present by backfillMarker/addMarker), but routing them through
 * the same accessor means there's only ever one place that has to get this
 * right. */
export function markerNoteText(marker: SongMarker, tab: MarkerNoteTab): string {
  return marker.notes[tab] ?? '';
}
export function markerNoteFormat(marker: SongMarker, tab: MarkerNoteTab): MarkerNoteFormat {
  return marker.noteFormats[tab] ?? 'text';
}
export function markerNoteSheet(marker: SongMarker, tab: MarkerNoteTab): MarkerSheetData {
  return marker.noteSheets[tab] ?? { rows: [] };
}

/** The 3 tabs every marker always has, in their fixed display order —
 * never user-removable, unlike CustomNoteTab entries. */
export const BUILT_IN_NOTE_TABS: readonly MarkerNoteTab[] = ['lyrics', 'chords', 'general'];

const BUILT_IN_NOTE_TAB_LABELS: Record<MarkerNoteTab, string> = { lyrics: 'Lyrics', chords: 'Chords', general: 'General' };

/** A user-defined note tab beyond the fixed Lyrics/Chords/General three
 * (e.g. "Bass", "Keys") — global across every song/setlist (not a per-song
 * setting), stored in AppSettings.customNoteTabs and managed from the Song
 * Library's marker editor. `id` is a generated id (see core/id.ts), not the
 * label itself, so renaming a tab never orphans its existing content the
 * way changing a dictionary KEY would — every marker's notes/noteFormats/
 * noteSheets are keyed by this stable id, with `label` looked up fresh each
 * render via noteTabLabel. Deleting a custom tab removes it from this list
 * (and so from every tab picker) but does NOT delete any content already
 * written under its id on any marker — recreating a tab with the same NAME
 * still gets a brand new id and starts empty, so that old content is
 * orphaned (inert, harmless, technically still in IndexedDB) rather than
 * silently resurrected under an unrelated new tab. */
export interface CustomNoteTab {
  id: string;
  label: string;
}

/** Every currently-selectable record-backed tab id, built-ins first — the
 * canonical list for building a tab-picker row/dropdown. Does NOT include
 * Performance mode's local-only "My Notes" pseudo-tab, which isn't a
 * CustomNoteTab and isn't stored on the marker record at all (see
 * PlayerNotesTab in performance.ts). */
export function allNoteTabs(customNoteTabs: readonly CustomNoteTab[]): MarkerNoteTab[] {
  return [...BUILT_IN_NOTE_TABS, ...customNoteTabs.map((t) => t.id)];
}

/** Display label for any record-backed tab id — a built-in's fixed label,
 * a custom tab's current (renameable) label, or the id itself as a last
 * resort if a marker somehow references a tab that's since been deleted
 * from customNoteTabs (rather than rendering a blank label). */
export function noteTabLabel(tab: MarkerNoteTab, customNoteTabs: readonly CustomNoteTab[]): string {
  return BUILT_IN_NOTE_TAB_LABELS[tab] ?? customNoteTabs.find((t) => t.id === tab)?.label ?? tab;
}

/** A named point in a song's timeline (e.g. "Verse 1", "Chorus 1"), used to
 * show song structure during playback and to jump/seek during practice. */
export interface SongMarker {
  id: string;
  label: string;
  /** Optional short code (e.g. "BRA" for "Bridge A") shown directly on the
   * Performance mode progress bar, where the full label wouldn't fit. Falls
   * back to a truncated auto-abbreviation of `label` when blank. */
  shortLabel: string;
  timeSeconds: number;
  notes: MarkerNotes;
  noteFormats: MarkerNoteFormats;
  noteSheets: MarkerNoteSheets;
}

/** A band/project a song belongs to, purely for organizing the Song Library
 * when someone plays in multiple bands — has no effect on playback, setlists,
 * or anything else. A song with `bandId: null` shows under "Unassigned." */
export interface BandRecord {
  id: string;
  name: string;
  createdAt: number;
}

export interface SongRecord {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  mode: PlaybackMode;
  /** Which band/project this song belongs to, for Song Library filtering
   * only — null means "Unassigned." See `BandRecord`. */
  bandId: string | null;
  /** Original filename of the imported audio, kept for reference; not used for identity. */
  originalFileName: string | null;
  fileFormat: AudioFileFormat | null;
  durationSeconds: number | null;
  channelCount: number | null;
  fileSizeBytes: number | null;
  status: SongStatus;
  statusMessage: string | null;
  metronomeSettings: MetronomeSettings;
  /** Sorted ascending by timeSeconds. */
  markers: SongMarker[];
  /** For a file-based song with no click baked into its own audio: an
   * optional synthesized click can be layered on top, generated from
   * `metronomeSettings` (bpm/beatsPerMeasure/accentFirstBeat/sound) and
   * routed right-channel-only, same as the standalone metronome. The song's
   * real downbeat essentially never lands exactly on 0.000s, so this offset
   * (seconds, may be negative) shifts the click's own beat-1 to line up with
   * it — tuned by ear in the Song Library's marker editor. Independent of
   * `mode`; only takes effect while `syntheticClickEnabled` is on. */
  syntheticClickEnabled: boolean;
  syntheticClickOffsetSeconds: number;
  /** The exact position (in the song's own audio timeline) of its real
   * first beat, marked by ear/eye in the Song Library rather than typed in
   * as a beat count — some songs start with a bar or two of nothing (or a
   * pickup phrase) before the actual downbeat, so a pre-roll count-in that
   * ignores this plays extra uncounted beats on top of whatever the song
   * already has baked in, and a chosen "8 count" no longer feels like a
   * true 8 count. The number of pickup beats to subtract from a chosen
   * count-in is DERIVED from this (how many beats fall between
   * `syntheticClickOffsetSeconds` and this marked time), not stored
   * directly — deriving it means the count-in adjustment self-corrects if
   * BPM/offset are retuned later, instead of going stale against a
   * hand-typed number. Null means "not marked" (no pickup). Only meaningful
   * alongside `syntheticClickEnabled` (that's the only case the app knows
   * this song's BPM at all). */
  firstBeatTimeSeconds: number | null;
  /** Any beat the user has confirmed as "beat 1 of the measure" once the
   * offset is lined up with the song — NOT necessarily the song's true
   * first beat (that's `firstBeatTimeSeconds`, a separate concept for
   * pre-roll pickup math). A user only needs to line the click up with
   * SOME beat, tap "Mark as Aligned," and that exact beat becomes the
   * reference "1" the zoomed Beat Alignment strip counts 1-2-3-4 from —
   * they don't need to hunt for the literal first beat of the whole song
   * just to get numbered ticks. Null means no reference has been set (the
   * strip shows plain unnumbered ticks). Only meaningful alongside
   * `syntheticClickEnabled`. */
  beatOneReferenceSeconds: number | null;
  /** Seconds to seek BEFORE a tapped marker's own timestamp (or to hold
   * silent/click-counting before this song's own audio starts on a fresh
   * Play), giving the rest of the band a run-up instead of a cold start.
   * Per-song (not global) — configured once in the Song Library, not
   * adjustable live during a show. This is the value actually used for
   * seek/countdown math regardless of which unit it was chosen in — see
   * `markerLeadInBeats` below. Only takes effect while the active setlist's
   * mode is 'rehearsal' (marker-tap seeking) — a pre-roll count-in at song
   * start applies regardless of Rehearsal/Live, same as before. */
  markerLeadInSeconds: number;
  /** When this song has a synthetic click (so its BPM is known), the
   * lead-in can be chosen as a beat-count (4/8/12/16) instead of raw
   * seconds — more natural for counting a band in. Null means the lead-in
   * is a plain seconds value (the only option for non-click songs); a
   * number here means it was last chosen as that many beats, which is what
   * the countdown displays/ticks in (recomputed each render from
   * `markerLeadInSeconds` and this song's own BPM, not stored as a frozen
   * assumption — so it stays correct even if BPM is edited later). Cleared
   * (set back to null) whenever a seconds option is chosen instead, or this
   * song has no synthetic click. */
  markerLeadInBeats: number | null;
  /** The Supabase `songs.id` this local song has been pushed to, or null if
   * it's never been synced to any band. Set once by `syncSongToBand` after
   * its first successful push; every re-sync after that updates the same
   * Supabase row rather than creating a duplicate. Purely a sync-tracking
   * field — has no bearing on this song's local behavior at all, and a
   * song can be renamed/edited locally without needing to re-sync unless
   * the user explicitly chooses to. */
  supabaseSongId: string | null;
}

/** Default per-song lead-in for a song that's never had one explicitly
 * chosen — 3 seconds, not the picker's own smallest listed option (5s):
 * deliberately short, since this is what a song silently gets before anyone
 * has actually thought about its count-in, not a value someone picked on
 * purpose. */
export const DEFAULT_MARKER_LEAD_IN_SECONDS = 3;

export interface AudioBlobRecord {
  songId: string;
  blob: Blob;
  mimeType: string;
}

export interface SetlistEntry {
  songId: string;
}

/** 'rehearsal' setlists are fully interactive in Performance mode (scrub the
 * progress bar, tap markers to jump) — the primary, default use case. 'live'
 * setlists lock that down (no scrubbing, no marker-tap) so nothing gets
 * accidentally seeked mid-show. Set once per setlist, not globally or
 * per-song — a setlist is built FOR either rehearsal or an actual show. */
export type SetlistMode = 'rehearsal' | 'live';

/** One side of a rehearsal routing choice — Stereo (untouched/natural),
 * Left, or Right. Rehearsal lets the track and click each independently
 * pick one of these three (see RehearsalRouting below); there's no PA to
 * protect in a practice setting, so unlike Live there's no reason to
 * restrict which combinations are allowed. */
export type RoutingSide = 'stereo' | 'left' | 'right';

/** Rehearsal routing: 'rehearsal-track-{X}-click-{Y}', independently
 * choosing the track's side and the click's side from RoutingSide — all 9
 * combinations are valid (e.g. narrowing the track to one side while ALSO
 * narrowing the click to the other, or even the same side, is a legitimate
 * choice here; only Live's genuine PA/in-ear split constrains this, see
 * SetlistRouting's own comment). Template-literal rather than 9 separate
 * hand-written strings so parseRehearsalRouting/buildRehearsalRouting can
 * mechanically convert to/from the {trackSide, clickSide} pair every other
 * routing value already collapses to in resolveOutputSides. */
export type RehearsalRouting = `rehearsal-track-${RoutingSide}-click-${RoutingSide}`;

/** Channel routing for every song played from this setlist — deliberately a
 * SETLIST-level setting, not a per-song one (which is how this worked
 * before): a live show's PA/in-ear split needs to stay identical song to
 * song, and per-song routing let that drift silently (one song left/right,
 * the next stereo, purely because of how each was originally imported).
 *
 * Two families:
 * - 'tracks-left-click-right' / 'tracks-right-click-left' — a genuine PA/
 *   in-ear split. The song's own audio is summed onto ONE side, freeing the
 *   other side entirely for the click (synthetic click layered on a file,
 *   or a metronome-only song's own click). This is the live-show case: the
 *   click must never bleed into the PA side, so unlike rehearsal, these
 *   two are the ONLY combinations offered — no free-form 3×3 picker.
 * - RehearsalRouting ('rehearsal-track-{X}-click-{Y}') — the track and
 *   click each independently pick Stereo/Left/Right (9 combinations
 *   total). This is the rehearsal case: there's no PA to protect, so
 *   someone practicing can freely choose exactly how they want to hear
 *   both the track and the click, including narrowing either one to a
 *   single side. 'rehearsal-track-stereo-click-stereo' is the default —
 *   both untouched, natural stereo.
 *
 * A song with nothing to split (no active click) always plays its natural
 * stereo mix regardless of which of these is chosen — see
 * PlaybackController.resolveOutputSides for exactly how a given song's
 * content collapses this into real per-engine output sides. */
export type SetlistRouting = RehearsalRouting | 'tracks-left-click-right' | 'tracks-right-click-left';

export const DEFAULT_REHEARSAL_ROUTING: RehearsalRouting = 'rehearsal-track-stereo-click-stereo';

export function parseRehearsalRouting(routing: SetlistRouting): { trackSide: RoutingSide; clickSide: RoutingSide } | null {
  const match = /^rehearsal-track-(stereo|left|right)-click-(stereo|left|right)$/.exec(routing);
  if (!match) return null;
  return { trackSide: match[1] as RoutingSide, clickSide: match[2] as RoutingSide };
}

export function buildRehearsalRouting(trackSide: RoutingSide, clickSide: RoutingSide): RehearsalRouting {
  return `rehearsal-track-${trackSide}-click-${clickSide}`;
}

/** Migrates a setlist's stored `routing` value (which may be `undefined`
 * for a setlist created before routing existed at all, OR one of the three
 * old fixed rehearsal literals — 'stereo-click-stereo' / 'stereo-click-left'
 * / 'stereo-click-right' — from before the free-form 3×3 RehearsalRouting
 * picker replaced them) into a valid current SetlistRouting. The two Live
 * literals ('tracks-left-click-right'/'tracks-right-click-left') and any
 * already-current RehearsalRouting value pass through unchanged. */
export function migrateSetlistRouting(routing: unknown): SetlistRouting {
  switch (routing) {
    case 'stereo-click-stereo':
      return 'rehearsal-track-stereo-click-stereo';
    case 'stereo-click-left':
      return 'rehearsal-track-stereo-click-left';
    case 'stereo-click-right':
      return 'rehearsal-track-stereo-click-right';
    case 'tracks-left-click-right':
    case 'tracks-right-click-left':
      return routing;
    default:
      return typeof routing === 'string' && parseRehearsalRouting(routing as SetlistRouting) ? (routing as SetlistRouting) : DEFAULT_REHEARSAL_ROUTING;
  }
}

/** Setlist-level count-in override — same precedent as `routing` above:
 * count-in used to be purely per-song (`markerLeadInSeconds`/
 * `markerLeadInBeats`), which let it drift song to song purely because of
 * when/how each was set up. This lets a setlist pin ONE count-in that
 * applies to every song in it instead, same shape a song's own lead-in
 * already uses (`seconds` is what's actually used for the seek/countdown
 * math; `beats` is display sugar when the count was chosen as a beat count
 * on a song that turns out to have a synthetic click, recomputed from
 * `seconds` + that song's own BPM rather than stored as a frozen value —
 * see `markerLeadInBeats`'s own comment for why).
 * - `null` — no override; each song uses its own `markerLeadInSeconds`/
 *   `markerLeadInBeats` exactly as before. The default for every setlist,
 *   old or new (see migrateSetlistCountIn).
 * - `'off'` — forces NO count-in for every song in this setlist, even one
 *   that has its own lead-in configured. Distinct from `{ seconds: 0, ...
 *   }` only in spelling; kept as its own literal so "explicitly off" reads
 *   clearly in the setlist editor UI rather than as a seconds value of 0.
 * - `{ seconds, beats }` — the setlist's own fixed count-in, applied to
 *   every song regardless of that song's own configured value. */
export type SetlistCountInOverride = null | 'off' | { seconds: number; beats: number | null };

/** Resolves the EFFECTIVE lead-in for a given song under a setlist's
 * count-in override — the setlist's choice always wins when set (per
 * SetlistCountInOverride's own comment), falling back to the song's own
 * markerLeadInSeconds/markerLeadInBeats only when the setlist has no
 * override at all (`null`). Shared by both call sites that need this
 * (song-start pre-roll, marker-tap lead-in) so they can't drift out of
 * sync with each other on how the override is applied. */
export function resolveEffectiveLeadIn(
  countInOverride: SetlistCountInOverride,
  song: Pick<SongRecord, 'markerLeadInSeconds' | 'markerLeadInBeats'>,
): { seconds: number; beats: number | null } {
  if (countInOverride === null) return { seconds: song.markerLeadInSeconds, beats: song.markerLeadInBeats };
  if (countInOverride === 'off') return { seconds: 0, beats: null };
  return countInOverride;
}

export interface SetlistRecord {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  entries: SetlistEntry[];
  mode: SetlistMode;
  routing: SetlistRouting;
  /** See SetlistCountInOverride's own comment. `null` (no override) for
   * every setlist created before this existed — see migrateSetlistCountIn. */
  countInOverride: SetlistCountInOverride;
}

/** Migrates a setlist's stored `countInOverride` (`undefined` for any
 * setlist saved before this field existed) into a valid current value —
 * same role as migrateSetlistRouting above, for the same reason (IndexedDB
 * records don't retroactively gain new fields on their own). Anything not
 * recognized falls back to `null` (no override, per-song behavior
 * unchanged) rather than guessing at `'off'` or a specific duration. */
export function migrateSetlistCountIn(countInOverride: unknown): SetlistCountInOverride {
  if (countInOverride === null || countInOverride === 'off') return countInOverride;
  if (
    typeof countInOverride === 'object' &&
    countInOverride !== null &&
    typeof (countInOverride as { seconds?: unknown }).seconds === 'number'
  ) {
    const beats = (countInOverride as { beats?: unknown }).beats;
    return { seconds: (countInOverride as { seconds: number }).seconds, beats: typeof beats === 'number' ? beats : null };
  }
  return null;
}

export interface AppSettings {
  activeSetlistId: string | null;
  activeEntryIndex: number;
  outputVolume: number; // always defaults to 1; kept for future-proofing, UI does not expose a mixer
  /** User-defined note tabs (see CustomNoteTab) — global, not per-song. */
  customNoteTabs: CustomNoteTab[];
}

export const DEFAULT_SETTINGS: AppSettings = {
  activeSetlistId: null,
  activeEntryIndex: 0,
  outputVolume: 1,
  customNoteTabs: [],
};

/** Selectable lead-in durations for the marker-tap picker, for a song with
 * no known BPM (no synthetic click enabled). */
export const MARKER_LEAD_IN_OPTIONS = [5, 6, 7, 8, 9, 10, 11, 12] as const;

/** Selectable lead-in durations, in beats, for a song WITH a synthetic click
 * enabled — 1/2/3/4 measures of 4/4, the natural way to count a band in. */
export const MARKER_LEAD_IN_BEAT_OPTIONS = [4, 8, 12, 16] as const;

export type PlaybackStatus = 'READY' | 'PLAYING' | 'PAUSED' | 'STOPPED' | 'ERROR' | 'ENDED';

export interface BackupFileV1 {
  formatVersion: 1;
  exportedAt: number;
  appVersion: string;
  songs: Omit<SongRecord, 'status' | 'statusMessage'>[];
  setlists: SetlistRecord[];
  bands: BandRecord[];
}
